import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Slider,
  Alert,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Snackbar,
  Grid,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import {
  generateReview,
  generateHighLevelPlan,
  generateReviewFromPlan,
  regenerateWithPlan
} from '../../api/openaiService';
import { useAppContext } from '../../contexts/AppContext';
import ReviewProgress from './ReviewProgress';
import HighLevelPlanEditor from './HighLevelPlanEditor';
import { downloadBibtex } from '../../utils/citationUtils';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

function ReviewGenerator({ abstract, setAbstract, setIsGenerating }) {
  const {
    selectedPapers,
    setSelectedPapers,
    generatedReview,
    setGeneratedReview,
    reviewLoading,
    setReviewLoading,
    setError,
    parameters,
    setParameters
  } = useAppContext();

  const [validationError, setValidationError] = useState('');
  const [progressStep, setProgressStep] = useState(0);
  const [plan, setPlan] = useState('');
  const [highLevelPlan, setHighLevelPlan] = useState('');
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedPapersExpanded, setSelectedPapersExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const papersPerPage = 10;
  // State to track which papers are expanded to show abstract
  const [expandedPapers, setExpandedPapers] = useState({});

  // Function to toggle expansion of a specific paper
  const togglePaperExpansion = (paperId) => {
    setExpandedPapers(prev => {
      const newState = {
        ...prev,
        [paperId]: !prev[paperId]
      };
      return newState;
    });
  };

  // Function to handle paper deselection
  const handleDeselectPaper = (paperId) => {
    // Remove the paper from the selected papers array
    setSelectedPapers(prev => prev.filter(paper => paper.paperId !== paperId));

    // Also remove it from the expanded papers object
    setExpandedPapers(prev => {
      const newState = { ...prev };
      delete newState[paperId];
      return newState;
    });
  };

  useEffect(() => {
    if (abstract?.trim() && selectedPapers?.length) {
      setValidationError('');
    }
  }, [abstract, selectedPapers]);

  const handleParameterChange = (param, value) => {
    setParameters(prev => ({
      ...prev,
      [param]: value
    }));
  };

  const validateInputs = () => {
    if (!abstract?.trim()) {
      setValidationError('Please enter your paper abstract');
      return false;
    }
    if (!selectedPapers?.length) {
      setValidationError('Please select papers from the search results first');
      return false;
    }
    return true;
  };

  const handleGenerateReview = async () => {
    if (!validateInputs()) return;

    setIsGenerating(true);
    setReviewLoading(true);
    setError(null);
    setValidationError('');
    setProgressStep(0);

    try {
      // Set progress steps immediately without delays to avoid potential ResizeObserver issues
      setProgressStep(1);
      // No artificial delay

      setProgressStep(2);
      // No artificial delay

      setProgressStep(3);
      const response = await generateReview({
        abstract,
        papers: selectedPapers,
        parameters
      });

      setProgressStep(4);
      if (response?.generatedReview) {
        setGeneratedReview(response.generatedReview);
        // Switch to the Review Results tab
        // You'll need to add a way to switch tabs
      } else {
        throw new Error('Failed to generate review');
      }
    } catch (error) {
      console.error('Error generating review:', error);
      setError(error?.response?.data?.error || error.message || 'Failed to generate review');
      setValidationError('Failed to generate review. Please try again.');
    } finally {
      setReviewLoading(false);
      setIsGenerating(false);
    }
  };

  const handleGenerateHighLevelPlan = async () => {
    if (!validateInputs()) return;

    setIsGeneratingPlan(true);
    setError(null);
    setValidationError('');
    setHighLevelPlan(''); // Clear any existing plan

    try {

      // Use the regular API call instead of streaming
      const response = await generateHighLevelPlan({
        abstract: abstract,
        papers: selectedPapers,
        parameters: {
          model: parameters.model || 'openai/gpt-4o-mini',
          temperature: parameters.temperature || 0.7,
          maxTokens: parameters.maxTokens || 1000
        }
      });


      if (response.success && response.generatedPlan) {
        setHighLevelPlan(response.generatedPlan);
      } else {
        throw new Error(response.error || 'Failed to generate high-level plan');
      }
    } catch (error) {
      console.error('Error generating high-level plan:', error);
      setError(error.message || 'Failed to generate high-level plan');
      setValidationError('Failed to generate high-level plan. Please try again.');
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Function to generate review from edited high-level plan without streaming
  const handleGenerateReviewFromPlan = async () => {
    if (!highLevelPlan.trim()) {
      setValidationError('Please generate or enter a high-level plan first');
      return;
    }

    if (!validateInputs()) return;

    setIsGenerating(true);
    setReviewLoading(true);
    setError(null);
    setValidationError('');

    try {

      // Use the regular API call instead of streaming
      const response = await generateReviewFromPlan({
        abstract: abstract,
        highLevelPlan: highLevelPlan,
        papers: selectedPapers,
        parameters: {
          model: parameters.model || 'openai/gpt-4o-mini',
          temperature: parameters.temperature || 0.7,
          maxTokens: parameters.maxTokens || 1000
        }
      });


      if (response.success && response.generatedReview) {
        setGeneratedReview(response.generatedReview);
      } else {
        throw new Error(response.error || 'Failed to generate review from plan');
      }
    } catch (error) {
      console.error('Error generating review from plan:', error);
      setError(error.message || 'Failed to generate review from plan');
      setValidationError('Failed to generate review from plan. Please try again.');
    } finally {
      setIsGenerating(false);
      setReviewLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedReview);
    setCopySuccess(true);
  };

  const handleExportAllCitations = async () => {
    if (selectedPapers.length === 0) return;

    try {
      await downloadBibtex(selectedPapers, 'selected_papers.bib');
    } catch (error) {
      console.error('Error exporting citations:', error);
      // Optionally show error to user
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Header styled to match "Search for Related Papers" */}
      <Box sx={{
        p: 2,
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 0.5,
          width: '100%',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ color: '#9c27b0', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
              Literature Review Generator
            </Typography>
          </Box>
          <Chip
            label={`${selectedPapers.length} Papers Selected`}
            size="small"
            sx={{
              bgcolor: selectedPapers.length > 0 ? 'rgba(156, 39, 176, 0.1)' : 'rgba(0, 0, 0, 0.08)',
              color: selectedPapers.length > 0 ? '#9c27b0' : 'text.secondary',
              fontWeight: 600,
              height: 24
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4 }}>
          Generate comprehensive analysis of selected research
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Selected Papers */}
        {/* Right Column - Model Parameters as collapsible */}
        <Grid item xs={12} md={12}>
          <Accordion
            defaultExpanded
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: '12px !important',
              border: '1px solid rgba(0,0,0,0.08)',
              bgcolor: '#fafafa',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '12px 12px 0 0'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 3,
                py: 1,
                '& .MuiAccordionSummary-content': {
                  my: 1
                }
              }}
            >
              <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
              }}>
                <TuneIcon sx={{ mr: 1.5, color: '#9c27b0', fontSize: '1.2rem' }} />
                Model Parameters
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom sx={{
                  color: 'text.secondary',
                  mb: 1,
                  fontSize: '0.85rem'
                }}>
                  Model
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={parameters.model || 'openai/gpt-4o-mini'}
                    onChange={(e) => handleParameterChange('model', e.target.value)}
                    sx={{
                      bgcolor: 'white',
                      fontSize: '0.875rem',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.12)',
                      },
                      borderRadius: '8px',
                    }}
                  >
                    <MenuItem value="openai/gpt-4o-mini">openai/gpt-4o-mini</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontSize: '0.85rem'
                  }}>
                    Temperature: {parameters.temperature ?? 0.7}
                  </Typography>
                </Box>
                <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                  <Slider
                    value={parameters.temperature ?? 0.7}
                    onChange={(e, value) => handleParameterChange('temperature', value)}
                    min={0}
                    max={1}
                    step={0.1}
                    sx={{
                      mx: 1,
                      color: '#9c27b0',
                      '& .MuiSlider-rail': {
                        opacity: 0.3,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontSize: '0.85rem'
                  }}>
                    Max Tokens: {parameters.maxTokens || 1000}
                  </Typography>
                </Box>
                <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                  <Slider
                    value={parameters.maxTokens || 1000}
                    onChange={(e, value) => handleParameterChange('maxTokens', value)}
                    min={100}
                    max={4000}
                    step={100}
                    sx={{
                      mx: 1,
                      color: '#9c27b0',
                      '& .MuiSlider-rail': {
                        opacity: 0.3,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Typography variant="caption" sx={{
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: '0.75rem',
                  lineHeight: 1.5
                }}>
                  Higher temperature values generate more creative but potentially less accurate reviews. More tokens result in longer reviews.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} md={12}>
          {/* Selected Papers */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => setSelectedPapersExpanded(!selectedPapersExpanded)}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: 'rgba(0,0,0,0.02)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MenuBookIcon sx={{ mr: 1.5, color: '#9c27b0' }} />
                <Typography variant="subtitle1" sx={{
                  fontWeight: 600,
                  color: 'rgba(0, 0, 0, 0.8)',
                }}
                >
                  Selected Papers ({selectedPapers.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedPapers.length > 0 && (
                  <Tooltip title="Export All Selected Citations (BibTeX)">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportAllCitations();
                      }}
                      sx={{ mr: 1, color: '#9c27b0' }}
                    >
                      <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {selectedPapersExpanded ?
                  <ExpandLessIcon fontSize="small" /> :
                  <ExpandMoreIcon fontSize="small" />
                }
              </Box>
            </Box>

            <Collapse in={selectedPapersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ display: selectedPapersExpanded ? 'block' : 'none' }}>
                {selectedPapers.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No papers selected. Search and select papers from the left panel.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {selectedPapers
                      .slice((currentPage - 1) * papersPerPage, currentPage * papersPerPage)
                      .map((paper, index) => (
                        <Box
                          key={paper.paperId || index}
                          sx={{
                            p: 3,
                            borderTop: '1px solid rgba(0,0,0,0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box sx={{
                              minWidth: 24,
                              height: 24,
                              borderRadius: '4px',
                              bgcolor: 'rgba(156, 39, 176, 0.08)',
                              color: '#9c27b0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              mr: 2,
                              fontSize: '0.8rem'
                            }}>
                              {(currentPage - 1) * papersPerPage + index + 1}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box
                                onClick={() => togglePaperExpansion(paper.paperId || `paper-${index}`)}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  borderRadius: '4px',
                                  '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.05)'
                                  }
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {paper.title}
                                </Typography>
                                {expandedPapers[paper.paperId || `paper-${index}`] ?
                                  <ExpandLessIcon fontSize="small" color="action" /> :
                                  <ExpandMoreIcon fontSize="small" color="action" />
                                }
                              </Box>

                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.8rem' }}>
                                {paper.authors?.map(a => a.name).join(', ')} ({paper.year || 'n/a'})
                              </Typography>

                              {/* Abstract - only shown when expanded */}
                              {expandedPapers[paper.paperId || `paper-${index}`] && (
                                <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: 'rgba(0,0,0,0.01)', borderRadius: '4px' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    Abstract:
                                  </Typography>
                                  <Typography variant="body2" sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    mt: 0.5
                                  }}>
                                    {paper.abstract || 'No abstract available for this paper'}
                                  </Typography>
                                </Box>
                              )}

                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Link
                                  href={paper.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#9c27b0',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  View Paper <LaunchIcon sx={{ ml: 0.5, fontSize: '0.8rem' }} />
                                </Link>
                                <Button
                                  size="small"
                                  onClick={() => handleDeselectPaper(paper.paperId || `paper-${index}`)}
                                  sx={{
                                    minWidth: 'auto',
                                    height: '20px',
                                    fontSize: '0.75rem',
                                    p: 0,
                                    color: 'rgba(255, 0, 0, 0.7)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 0, 0, 0.05)',
                                      color: 'rgba(255, 0, 0, 0.9)'
                                    }
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} /> Remove
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    {/* Pagination Controls */}
                    {selectedPapers.length > papersPerPage && (
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        mt: 2,
                        borderTop: '1px solid rgba(0,0,0,0.05)',
                        pt: 2
                      }}>
                        <Button
                          size="small"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            color: '#9c27b0',
                            '&.Mui-disabled': {
                              color: 'rgba(0, 0, 0, 0.26)'
                            }
                          }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Page {currentPage} of {Math.ceil(selectedPapers.length / papersPerPage)}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setCurrentPage(prev =>
                            Math.min(Math.ceil(selectedPapers.length / papersPerPage), prev + 1)
                          )}
                          disabled={currentPage >= Math.ceil(selectedPapers.length / papersPerPage)}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            color: '#9c27b0',
                            '&.Mui-disabled': {
                              color: 'rgba(0, 0, 0, 0.26)'
                            }
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Paper>

          {/* Paper Abstract */}
          <Paper elevation={0} sx={{
            p: 3,
            mb: 3,
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.08)',
          }}>
            <Typography variant="subtitle1" gutterBottom sx={{
              fontWeight: 600,
              color: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}>
              <DescriptionIcon sx={{ mr: 1.5, color: '#9c27b0', fontSize: '1.2rem' }} />
              Your Paper Abstract
            </Typography>

            <TextField
              multiline
              rows={6}
              fullWidth
              placeholder="Enter your paper abstract to generate a relevant literature review"
              value={abstract || ''}
              onChange={(e) => setAbstract(e.target.value)}
              variant="outlined"
              sx={{
                '.MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  bgcolor: 'rgba(0,0,0,0.01)',
                },
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.12)',
                }
              }}
            />

            {validationError && (
              <Alert severity="error" sx={{ mt: 2 }}>{validationError}</Alert>
            )}
          </Paper>

          {/* High-level plan UI */}
          <Accordion defaultExpanded={true} sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="high-level-plan-content"
              id="high-level-plan-header"
            >
              <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                <FormatListBulletedIcon sx={{ mr: 1 }} />
                High-Level Plan (Optional)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <HighLevelPlanEditor
                highLevelPlan={highLevelPlan}
                setHighLevelPlan={setHighLevelPlan}
                isGeneratingPlan={isGeneratingPlan}
                handleGeneratePlan={handleGenerateHighLevelPlan}
                handleGenerateReviewFromPlan={handleGenerateReviewFromPlan}
                isGeneratingReview={reviewLoading}
                validationError={validationError}
              />
            </AccordionDetails>
          </Accordion>

          {/* Standard review generation button at the bottom */}
          <Box sx={{ mt: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleGenerateReview}
              disabled={reviewLoading}
              startIcon={reviewLoading ? <CircularProgress size={24} color="inherit" /> : <AutoAwesomeIcon />}
              sx={{ py: 1.5, px: 4 }}
            >
              {reviewLoading ? 'Generating Review...' : 'Generate Review Directly'}
            </Button>
          </Box>
        </Grid>


      </Grid>

      {/* Progress and Results Section */}
      <Box sx={{
        display: reviewLoading ? 'block' : 'none',
        mt: 4
      }}>
        <ReviewProgress currentStep={progressStep} />
      </Box>

      {/* Generated Review Section */}
      {generatedReview && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, padding: 1 }}>
              Generated Literature Review
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyToClipboard}
              sx={{
                borderRadius: 'var(--border-radius-md)',
                textTransform: 'none',
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                borderColor: 'var(--neutral-300)',
                color: 'var(--neutral-700)'
              }}
            >
              Copy
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 'var(--border-radius-md)',
              border: '1px solid var(--neutral-200)',
              backgroundColor: '#fafbfc',
              maxHeight: '600px',
              overflow: 'auto'
            }}
          >
            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {generatedReview}
            </Typography>
          </Paper>

        </Box>
      )}

      {/* Sentence-level plan UI in a fixed position */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="sentence-plan-content"
            id="sentence-plan-header"
          >
            <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
              <FormatListNumberedIcon sx={{ mr: 1 }} />
              Sentence-Level Plan (Optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              You can optionally specify a sentence-level plan to guide the literature review generation.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              placeholder="Enter sentence plan (Optional). Example: The review will have 10 sentences. Sentence 1 will cite [1,2]. Sentence 2-3 will cite [3]..."
              variant="outlined"
              margin="normal"
            />
            {/* Add a button to generate directly with the sentence plan */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={reviewLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                onClick={async () => {
                  if (!validateInputs()) return;
                  if (!plan.trim()) {
                    setValidationError('Please enter a sentence plan');
                    return;
                  }

                  setIsGenerating(true);
                  setReviewLoading(true);
                  setError(null);
                  setValidationError('');
                  setProgressStep(0);

                  try {
                    setProgressStep(1);
                    setProgressStep(2);
                    setProgressStep(3);

                    const response = await regenerateWithPlan({
                      abstract,
                      plan: plan.trim(),
                      papers: selectedPapers,
                      parameters
                    });

                    setProgressStep(4);
                    if (response?.generatedReview) {
                      setGeneratedReview(response.generatedReview);
                    } else {
                      throw new Error('Failed to generate review with plan');
                    }
                  } catch (error) {
                    console.error('Error generating review with plan:', error);
                    setError(error?.response?.data?.error || error.message || 'Failed to generate review with plan');
                    setValidationError('Failed to generate review with plan. Please try again.');
                  } finally {
                    setReviewLoading(false);
                    setIsGenerating(false);
                  }
                }}
                disabled={reviewLoading || !plan.trim()}
                sx={{ ml: 1 }}
              >
                {reviewLoading ? 'Generating...' : 'Generate with Plan'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {copySuccess && (
        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          message="Literature review copied to clipboard"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      )}
    </Box>
  );
}

export default ReviewGenerator; 